import type {Dispatch, ThunkAction} from 'src/reducers';
import type {
  WritebackFieldValue,
  FieldWritebackChangeToOperatorType,
} from 'src/types/field-writeback';
import type {FieldWritebackState} from 'src/reducers/field-writeback';
import type {NodeSchedule} from 'src/types/node-schedule';


type UpdateFieldWritebackPayload = Partial<FieldWritebackState>;

type FieldWritebackCriteriaType = 'field' | 'person';
export const RECEIVE_FIELD_WRITEBACK_INFO = 'field-writeback/receive-info';

export const SET_FIELD_WRITEBACK = 'field-writeback/set';
export const UPDATE_FIELD_WRITEBACK = 'field-writeback/update';
export const UPDATE_NODE_SCHEDULE = 'field-writeback/update-node-schedule';
export const RESET_FIELD_WRITEBACK = 'field-writeback/reset';
export const SET_FIELD_WRITEBACK_CHANGE_TYPE =
  'field-writeback/set-criteria-type';
export const SET_FIELD_WRITEBACK_FIELD = 'field-writeback/set-writeback-field';
export const SET_FIELD_WRITEBACK_VALUE = 'field-writeback/set-writeback-value';
export const SET_FIELD_WRITEBACK_CHANGE_TO_OPERATOR =
  'field-writeback/set-change-to-operator';
export const SET_FIELD_WRITEBACK_EXTERNAL_SOURCE_ID_SENSE_ID_MAP =
  'field-writeback/set-field-wb-metamap';

type RecieveFieldWritebackInfoAction = {
  type: typeof RECEIVE_FIELD_WRITEBACK_INFO;
  payload: FieldWritebackState;
};
type UpdateFieldWritebackAction = {
  type: 'field-writeback/update';
  payload: UpdateFieldWritebackPayload;
};
type UpdateNodeScheduleAction = {
  type: typeof UPDATE_NODE_SCHEDULE;
  payload: Partial<NodeSchedule>;
};

type ResetFieldWritebackAction = {
  type: 'field-writeback/reset';
  payload?: FieldWritebackState;
};
type SetFieldWritebackAction = {
  type: 'field-writeback/set';
  payload: FieldWritebackState;
};
type SetFieldWritebackCriteriaTypeAction = {
  type: 'field-writeback/set-criteria-type';
  payload: FieldWritebackCriteriaType;
};
type SetFieldWritebackFieldsAction = {
  type: 'field-writeback/set-writeback-field';
  payload: WritebackFieldValue;
};
type SetFieldWritebackValueAction = {
  type: 'field-writeback/set-writeback-value';
  payload: {
    id: string;
    relationScopedId: string;
    value: Array<string>;
  };
};
type SetFieldWritebackChangeToOperatorAction = {
  type: 'field-writeback/set-change-to-operator';
  payload: {
    id: string;
    relationScopedId: string;
    value: FieldWritebackChangeToOperatorType;
  };
};

type SetFieldWritebackMetaMap = {
  type: 'field-writeback/set-field-wb-metamap';
  payload: {
    [key: string]: string | null | undefined;
  };
};

export type FieldWritebackAction =
  | RecieveFieldWritebackInfoAction
  | UpdateFieldWritebackAction
  | UpdateNodeScheduleAction
  | ResetFieldWritebackAction
  | SetFieldWritebackAction
  | SetFieldWritebackCriteriaTypeAction
  | SetFieldWritebackFieldsAction
  | SetFieldWritebackValueAction
  | SetFieldWritebackChangeToOperatorAction
  | SetFieldWritebackMetaMap;

export const receiveFieldWriteback = (
  payload: FieldWritebackState,
): RecieveFieldWritebackInfoAction => ({
  type: RECEIVE_FIELD_WRITEBACK_INFO,
  payload,
});
export const updateFieldWriteback = (
  payload: UpdateFieldWritebackPayload,
): UpdateFieldWritebackAction => ({
  type: UPDATE_FIELD_WRITEBACK,
  payload,
});
export const updateNodeSchedule = (
  payload: Partial<NodeSchedule>,
): UpdateNodeScheduleAction => ({
  type: UPDATE_NODE_SCHEDULE,
  payload,
});
export const resetFieldWriteback = (): ResetFieldWritebackAction => ({
  type: RESET_FIELD_WRITEBACK,
});

export const setFieldWriteback = (
  payload: FieldWritebackState,
): SetFieldWritebackAction => ({
  type: SET_FIELD_WRITEBACK,
  payload,
});
export const setFieldWritebackCriteriaType = (
  payload: FieldWritebackCriteriaType,
): SetFieldWritebackCriteriaTypeAction => ({
  type: SET_FIELD_WRITEBACK_CHANGE_TYPE,
  payload,
});
export const setFieldWritebackField = (
  payload: WritebackFieldValue,
): SetFieldWritebackFieldsAction => ({
  type: SET_FIELD_WRITEBACK_FIELD,
  payload,
});
export const setFieldWritebackValue = (payload: {
  id: string;
  relationScopedId: string;
  value: Array<string>;
}): SetFieldWritebackValueAction => ({
  type: SET_FIELD_WRITEBACK_VALUE,
  payload,
});
export const setFieldWritebackChangeToOperator = (payload: {
  id: string;
  relationScopedId: string;
  value: FieldWritebackChangeToOperatorType;
}): SetFieldWritebackChangeToOperatorAction => ({
  type: SET_FIELD_WRITEBACK_CHANGE_TO_OPERATOR,
  payload,
});

export const getFieldWriteback: (
  id: string,
  swid: number,
) => ThunkAction<unknown> | unknown =
  (id, swid) => (dispatch: Dispatch, getState: any) => {
    //we already have data for fwb in the swimlane data so we pick it from there instead of getting it from the api
    // we can also hit the api from here to get the data if we want
    const fieldWritebackData = getState().automationWorkflow.swimlanes.find(
      // @ts-expect-error - TS7006 - Parameter 'swimlane' implicitly has an 'any' type.
      (swimlane) => swimlane.id === swid,
    ).nodes[id];
    return fieldWritebackData ?? null;
  };

export const setFieldWritebackMetaMap = (payload: {
  [key: string]: string | null | undefined;
}): SetFieldWritebackMetaMap => ({
  type: SET_FIELD_WRITEBACK_EXTERNAL_SOURCE_ID_SENSE_ID_MAP,
  payload,
});
